import { memo, useEffect } from 'react';
import { Button, Space, Typography, App } from 'antd';
import { ExclamationCircleOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import { useRequest, useItemSelector } from '@wappsto/blanket';
import { Trans } from 'react-i18next';
import { useTranslation } from 'translations';
import { getRequestErrorMessage } from 'util/helpers';
import { DEVICES_ADD_ITEM_NAME } from 'util/params';
import GenericNotification from './GenericNotification';

const READ = 'read';
const DELETED = 'deleted';
const DENIED = 'denied';
const ACCEPTED = 'accepted';

interface SharePermissionProps {
  notification: any;
  updateNotificationStatus: (...args: any[]) => any;
  deleteNotification: (...args: any[]) => any;
  onClick: (...args: any[]) => any;
  isDeleting: boolean;
}

const SharePermission = memo(
  ({ notification, updateNotificationStatus, deleteNotification, onClick, isDeleting }: SharePermissionProps) => {
    const { tC } = useTranslation();
    const { send, request } = useRequest();
    const addItem = useItemSelector(DEVICES_ADD_ITEM_NAME);
    const { message } = App.useApp();

    const mainID = notification?.base?.ids?.at(0) || '';

    const description = notification.base.info
      ? tC('features:notifications.sharePermission', {
          user_name: notification.base.from_name || notification.base.from,
          type: notification.base.info[0].type || '',
          name: notification.base.info[0].name || '',
        })
      : tC('features:notifications.shareUnknownPermission', {
          type: notification.base.type_ids,
        });

    const error =
      request &&
      request.status === 'error' &&
      request.responseStatus !== 404 &&
      request.responseStatus !== 401;

    const acceptNotification = (e) => {
      e.stopPropagation();
      send({
        method: 'POST',
        url: `/${notification.base.type_ids}/${mainID}`,
        body: {
          meta: {
            id: mainID,
          },
        },
      });
    };

    const denyPermission = (e) => {
      e.stopPropagation();
      updateNotificationStatus(notification, DENIED, null, true);
    };

    useEffect(() => {
      if (request) {
        if (request.status === 'success') {
          updateNotificationStatus(notification, ACCEPTED);
          if (addItem) {
            addItem(request.json);
          }
        } else if (request.status === 'error') {
          let errorMessage;
          if (request.responseStatus === 404 || request.responseStatus === 401) {
            updateNotificationStatus(notification, DELETED);
            errorMessage = tC('features:notifications.notificationDeleteError');
          } else {
            updateNotificationStatus(notification, READ);
            const m = getRequestErrorMessage(request, tC);
            errorMessage = m.message;
          }
          message.open({
            key: `notificationAcceptError_${notification.meta.id}`,
            type: 'error',
            duration: 5,
            content: (
              <>
                <Trans i18nKey="features:notifications.notificationAcceptError" />
                <Typography.Paragraph type="danger">{errorMessage}</Typography.Paragraph>
              </>
            ),
          });
        }
      }
    }, [request, addItem, notification, tC, updateNotificationStatus, message]);

    return (
      <GenericNotification
        description={description}
        notification={notification}
        deleteNotification={deleteNotification}
        isDeleting={isDeleting}
        onClick={onClick}
        extra={
          notification.read !== ACCEPTED &&
          notification.read !== DELETED && (
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              {notification.read !== DENIED && (
                <Button size="small" icon={<DislikeOutlined />} onClick={denyPermission}>
                  {tC('features:notifications.notificationDenyButton')}
                </Button>
              )}
              <Button
                size="small"
                type="primary"
                onClick={acceptNotification}
                loading={request?.status === 'pending'}
                icon={
                  error ? <ExclamationCircleOutlined style={{ color: 'red' }} /> : <LikeOutlined />
                }>
                {tC('features:notifications.notificationAcceptButton')}
              </Button>
            </Space>
          )
        }
      />
    );
  },
);

SharePermission.displayName = 'SharePermission';

export default SharePermission;
